import React, { useCallback } from "react";
import { useParams } from "react-router-dom";
import { StringParam, useQueryParams, withDefault } from "use-query-params";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { useSubscribeMutation } from "@api/flatRateBilling.api";
import { useScope } from "@state/scopeContext.lib";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useEffectOnce } from "@hooks/useEffectOnce";
import { useUser } from "@hooks/useUser";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { PageCoverLoader } from "@design-system/PageCoverLoader/PageCoverLoader";

interface CheckoutRouteParams {
    plan: FLAT_RATE_PLANS;
}

export const Subscribe: React.FC = () => {
    const navigate = useStateAwareNavigate();
    const { plan } = useParams() as Readonly<CheckoutRouteParams>;
    const [{ after: afterCheckoutUrl, code, intent: intentId }] =
        useQueryParams({
            after: StringParam,
            code: withDefault(StringParam, undefined),
            intent: StringParam,
        });

    const { onboardingComplete } = useUser();
    const { query } = useBillingStatus();
    const afterCheckout = useCallback(async () => {
        await query.refetch();
        if (onboardingComplete) {
            navigate(afterCheckoutUrl ?? "/settings/billing");
        } else {
            navigate("/onboarding");
        }
    }, [afterCheckoutUrl, navigate, onboardingComplete, query]);
    const handleError = useCallback(() => {
        navigate("/settings/billing");
    }, [navigate]);
    const { activeWorkspaceKey } = useScope();
    const subscribeMutation = useSubscribeMutation(
        activeWorkspaceKey,
        afterCheckout,
        handleError,
    );

    useEffectOnce(() => {
        subscribeMutation.mutate({
            plan,
            code,
            intentId: intentId!,
        });
    });

    return (
        <PageCoverLoader>
            {onboardingComplete
                ? "Starting autopilot"
                : "Let's set up your account"}
        </PageCoverLoader>
    );
};
